import { parseBillingSummary, parseDailyClaimCalendar } from '@drama/contracts'

/**
 * @typedef {import('@drama/contracts/platform').SubscriptionT} Subscription
 */

function queryString(filter = {}) {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filter || {})) {
    if (value == null || value === '') continue
    params.set(key, String(value))
  }
  const query = params.toString()
  return query ? `?${query}` : ''
}

/**
 * pay 域：薄转发（Stripe.js 编排归 @drama/pay-client；本包不碰任何厂商 SDK/域名）。
 * @param {ReturnType<import('../transport.js').createTransport>} transport
 */
export function createPayApi(transport) {
  return {
    /**
     * 创建结账会话，返回后端给出的 Stripe checkout 会话引用（由 pay-client 接力跳转）。
     * @param {{ price_id:string, success_url?:string, cancel_url?:string }} req
     * @returns {Promise<{ checkout_session_id:string, url?:string }>}
     */
    createCheckout(req) {
      return transport.request('POST', '/api/sk/pay/checkout', { body: req })
    },

    /**
     * @returns {Promise<Subscription>}
     */
    getSubscription() {
      return transport.request('GET', '/api/sk/pay/subscription')
    },

    /**
     * 用点数解锁一条剧情线/一集（S2 真路由 POST /api/sk/unlock）。
     * 契约：`{ drama_id, episode_id? }` → `{ unlocked, remaining_credits, reason?:'insufficient'|'already' }`。
     *
     * 后端余额不足时返 HTTP 402（body 同样是契约形状），transport 会抛 ApiError；
     * 这里把它翻译回 `{ unlocked:false, reason:'insufficient' }`，使调用方（Player/DramaDetail）
     * 只需读 `res.unlocked/res.reason`，无需感知 HTTP 状态。
     *
     * `_user_credits/_unlock_cost` 仅作未登录/离线 fallback 的本地判定；真路由忽略它们。
     * @param {{ drama_id:string, episode_id?:string|number, _user_credits?:number, _unlock_cost?:number }} req
     * @returns {Promise<{ unlocked:boolean, remaining_credits:number, reason?:'insufficient'|'already' }>}
     */
    async unlock(req) {
      try {
        return await transport.request('POST', '/api/sk/unlock', {
          body: { drama_id: req.drama_id, episode_id: req.episode_id },
        })
      } catch (err) {
        // 402 余额不足：后端已带契约 body，但 transport 抛了 ApiError；归一回契约结果。
        if (err && err.status === 402) {
          const credits = typeof req._user_credits === 'number' ? req._user_credits : 0
          return { unlocked: false, remaining_credits: credits, reason: 'insufficient' }
        }
        throw err
      }
    },

    /**
     * 充值点数包结账（POST /api/sk/credits/checkout {pack_id} → Stripe session）。
     * @param {{ pack_id:string, success_url?:string, cancel_url?:string }} req
     * @returns {Promise<{ checkout_session_id:string, url?:string }>}
     */
    creditsCheckout(req) {
      return transport.request('POST', '/api/sk/credits/checkout', { body: req })
    },

    getAutoReload() {
      return transport.request('GET', '/api/sk/credits/auto-reload')
    },

    setupAutoReload(req) {
      return transport.request('POST', '/api/sk/credits/auto-reload/setup', { body: req })
    },

    updateAutoReload(req) {
      return transport.request('PATCH', '/api/sk/credits/auto-reload', { body: req })
    },

    disableAutoReload() {
      return transport.request('POST', '/api/sk/credits/auto-reload/disable', { body: {} })
    },

    listProducts() {
      return transport.request('GET', '/api/sk/billing/products')
    },

    saveProduct(product) {
      return transport.request('POST', '/api/sk/billing/products', { body: product })
    },

    updateProduct(productId, patch) {
      return transport.request('PATCH', `/api/sk/billing/products/${encodeURIComponent(productId)}`, { body: patch })
    },

    getBalance() {
      return transport.request('GET', '/api/sk/billing/balance')
    },

    getBillingSummary() {
      return transport.request('GET', '/api/sk/billing/summary', { parse: parseBillingSummary })
    },

    dailyClaim() {
      return transport.request('POST', '/api/sk/billing/daily-claim', { body: {} })
    },

    dailyClaimCalendar() {
      return transport.request('GET', '/api/sk/billing/daily-claim/calendar', { parse: parseDailyClaimCalendar })
    },

    /**
     * @callchain.routeId billing.ledger.list
     */
    listLedger(filter = {}) {
      return transport.request('GET', `/api/sk/billing/ledger${queryString(filter)}`, {
        routeId: 'billing.ledger.list',
      })
    },

    /**
     * @callchain.routeId payment.orders.list
     */
    listOrders(filter = {}) {
      return transport.request('GET', `/api/sk/pay/orders${queryString(filter)}`, {
        routeId: 'payment.orders.list',
      })
    },

    getOrder(orderId) {
      return transport.request('GET', `/api/sk/pay/orders/${encodeURIComponent(orderId)}`)
    },

    getReconciliation(filter = {}) {
      return transport.request('GET', `/api/sk/pay/reconciliation${queryString(filter)}`)
    },
  }
}
